import React, { useState, useMemo } from 'react';
import { 
  Copy, 
  Download, 
  Code, 
  Shield,
  FileJson,
  Share2,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

interface STIXExportProps {
  post: {
    id: string;
    username: string;
    content: string;
    created_at: string;
    analyzed_at?: string;
    url?: string;
    threat_level: string;
    summary?: string;
    analysis_id?: string;
    source_feed?: string;
    ttps?: string[];
    iocs?: string[];
    recommendations?: string[];
    threat_indicators?: Array<{
      indicator_type: string;
      indicator_value: string;
      confidence_score: number;
      verified: boolean;
      created_at: string;
    }>;
    website_crawl?: {
      url: string;
      title?: string;
      content?: string;
      threat_score: number;
    };
    image_analysis?: Array<{
      threat_score: number;
      extracted_text?: string;
    }>;
  };
  analysisSteps?: Array<{
    type: string;
    details: {
      extractedData?: any;
      confidence?: number;
      metadata?: any;
    };
  }>;
}

// STIX 2.1 format generation utilities
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const getCurrentTimestamp = () => new Date().toISOString();

const mapThreatLevelToTLP = (threatLevel: string): string => {
  switch (threatLevel.toLowerCase()) {
    case 'critical': return 'TLP:RED';
    case 'high': return 'TLP:AMBER';
    case 'medium': return 'TLP:GREEN';
    case 'low': return 'TLP:WHITE';
    default: return 'TLP:WHITE';
  }
};

const mapTTPToAttackPattern = (ttp: string) => {
  // Extract MITRE ATT&CK technique ID if present
  const mitreMatch = ttp.match(/T\d{4}(?:\.\d{3})?/);
  const techniqueId = mitreMatch ? mitreMatch[0] : null;
  
  return {
    type: 'attack-pattern',
    spec_version: '2.1',
    id: `attack-pattern--${generateUUID()}`,
    created: getCurrentTimestamp(),
    modified: getCurrentTimestamp(),
    name: ttp,
    description: `Tactic, Technique, or Procedure identified in threat analysis: ${ttp}`,
    ...(techniqueId && {
      external_references: [
        {
          source_name: 'mitre-attack',
          external_id: techniqueId,
          url: `https://attack.mitre.org/techniques/${techniqueId.replace('.', '/')}/`
        }
      ]
    }),
    kill_chain_phases: [
      {
        kill_chain_name: 'mitre-attack',
        phase_name: 'unknown'
      }
    ]
  };
};

const mapIOCToIndicator = (ioc: string, confidence: number = 75) => {
  // Determine IOC type based on pattern
  let pattern = ioc;
  let indicatorType = 'unknown';
  
  // IP address
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ioc)) {
    indicatorType = 'ipv4-addr';
    pattern = `[ipv4-addr:value = '${ioc}']`;
  }
  // Domain name
  else if (/^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*$/.test(ioc)) {
    indicatorType = 'domain-name';
    pattern = `[domain-name:value = '${ioc}']`;
  }
  // Hash (MD5, SHA1, SHA256)
  else if (/^[a-fA-F0-9]{32}$/.test(ioc)) {
    indicatorType = 'file';
    pattern = `[file:hashes.MD5 = '${ioc}']`;
  }
  else if (/^[a-fA-F0-9]{40}$/.test(ioc)) {
    indicatorType = 'file';
    pattern = `[file:hashes.SHA-1 = '${ioc}']`;
  }
  else if (/^[a-fA-F0-9]{64}$/.test(ioc)) {
    indicatorType = 'file';
    pattern = `[file:hashes.SHA-256 = '${ioc}']`;
  }
  // URL
  else if (/^https?:\/\//.test(ioc)) {
    indicatorType = 'url';
    pattern = `[url:value = '${ioc}']`;
  }
  // Email
  else if (/@/.test(ioc)) {
    indicatorType = 'email-addr';
    pattern = `[email-addr:value = '${ioc}']`;
  }

  return {
    type: 'indicator',
    spec_version: '2.1',
    id: `indicator--${generateUUID()}`,
    created: getCurrentTimestamp(),
    modified: getCurrentTimestamp(),
    pattern: pattern,
    labels: ['malicious-activity'],
    confidence: confidence,
    description: `Indicator of Compromise (IOC) extracted from threat intelligence analysis: ${ioc}`,
    indicator_types: ['malicious-activity'],
    valid_from: getCurrentTimestamp()
  };
};

export function STIXExport({ post, analysisSteps = [] }: STIXExportProps) {
  const [showRawJSON, setShowRawJSON] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['bundle']));

  const stixBundle = useMemo(() => {
    const timestamp = getCurrentTimestamp();
    const bundleId = `bundle--${generateUUID()}`;
    
    // Create base objects
    const objects: any[] = [];
    
    // 1. Report object (main container)
    const reportId = `report--${generateUUID()}`;
    const report = {
      type: 'report',
      spec_version: '2.1',
      id: reportId,
      created: post.analyzed_at || post.created_at,
      modified: timestamp,
      name: `Threat Intelligence Analysis - ${post.id}`,
      description: post.summary || `Automated threat intelligence analysis of social media post from @${post.username}`,
      report_types: ['threat-report'],
      published: timestamp,
      object_refs: [], // Will be populated below
      confidence: 85,
      labels: [mapThreatLevelToTLP(post.threat_level)],
      external_references: [
        ...(post.url ? [{
          source_name: 'original-post',
          description: 'Original social media post URL',
          url: post.url
        }] : []),
        {
          source_name: 'osm-threat-intelligence',
          description: 'OSM Threat Intelligence Platform Analysis',
          url: `https://osm-threat-dashboard.pages.dev/analysis/${post.analysis_id}`
        }
      ]
    };
    objects.push(report);

    // 2. Observed Data object (the original post)
    const observedDataId = `observed-data--${generateUUID()}`;
    const observedData = {
      type: 'observed-data',
      spec_version: '2.1',
      id: observedDataId,
      created: post.created_at,
      modified: timestamp,
      first_observed: post.created_at,
      last_observed: post.created_at,
      number_observed: 1,
      confidence: 95,
      objects: {
        '0': {
          type: 'x-social-media-post',
          id: post.id,
          content: post.content,
          author: post.username,
          platform: post.source_feed || 'unknown',
          url: post.url,
          timestamp: post.created_at,
          threat_level: post.threat_level
        }
      }
    };
    objects.push(observedData);

    // 3. Malware object - ONLY create if we have specific malware indicators
    const enhancedAIStep = analysisSteps.find(step => step.type === 'enhanced_ai');
    const threatTypes = enhancedAIStep?.details?.extractedData?.threat_types || [];
    const contentContext = enhancedAIStep?.details?.extractedData?.content_context || 'incident';
    
    // Check for real IOCs in the post
    const hasRealIOCs = (post.iocs && post.iocs.length > 0) || 
                        (enhancedAIStep?.details?.extractedData?.iocs?.length > 0) ||
                        (post.threat_indicators && post.threat_indicators.length > 0);
    
    // Check for specific malware family names (not just "unknown")
    const specificMalwarePatterns = [
      'ransomware', 'trojan', 'worm', 'rootkit', 'spyware', 'adware',
      'botnet', 'backdoor', 'keylogger', 'cryptominer', 'dropper',
      'emotet', 'wannacry', 'lockbit', 'conti', 'ryuk', 'revil'
    ];
    const hasSpecificMalware = threatTypes.some((t: string) => 
      specificMalwarePatterns.some(pattern => t.toLowerCase().includes(pattern))
    );
    
    // Only create Malware object if we have BOTH specific malware AND real IOCs
    if (hasSpecificMalware && hasRealIOCs) {
      const malwareId = `malware--${generateUUID()}`;
      const malware = {
        type: 'malware',
        spec_version: '2.1',
        id: malwareId,
        created: timestamp,
        modified: timestamp,
        name: `Malware referenced in ${post.id}`,
        description: `Malware activity identified through threat intelligence analysis with confirmed IOCs`,
        malware_types: threatTypes.filter((type: string) => type !== 'unknown' && type !== 'malware'),
        is_family: false,
        confidence: enhancedAIStep?.details?.confidence || 75,
        labels: threatTypes.filter((type: string) => type !== 'malware')
      };
      objects.push(malware);
      report.object_refs.push(malwareId);
    } else if ((post.threat_level === 'critical' || post.threat_level === 'high') && !hasRealIOCs) {
      // For high-threat content WITHOUT specific malware or IOCs, create a Note instead
      // This prevents false positives for news/prediction articles
      const noteId = `note--${generateUUID()}`;
      const note = {
        type: 'note',
        spec_version: '2.1',
        id: noteId,
        created: timestamp,
        modified: timestamp,
        abstract: `High threat level content - ${contentContext}`,
        content: `This content was classified as ${post.threat_level} threat level but does not contain specific malware indicators or actionable IOCs. Content context: ${contentContext}. This may be news, predictions, or educational material discussing threats rather than an active incident.`,
        object_refs: [reportId]
      };
      objects.push(note);
      report.object_refs.push(noteId);
    }

    // 4. Threat Actor object if attribution found
    const attribution = enhancedAIStep?.details?.extractedData?.attribution;
    if (attribution && attribution !== 'Unknown') {
      const threatActorId = `threat-actor--${generateUUID()}`;
      const threatActor = {
        type: 'threat-actor',
        spec_version: '2.1',
        id: threatActorId,
        created: timestamp,
        modified: timestamp,
        name: attribution,
        description: `Threat actor identified or attributed in threat intelligence analysis`,
        threat_actor_types: ['unknown'],
        confidence: enhancedAIStep?.details?.confidence || 50,
        labels: threatTypes
      };
      objects.push(threatActor);
      report.object_refs.push(threatActorId);
    }

    // 5. Attack Pattern objects for TTPs
    if (post.ttps && post.ttps.length > 0) {
      post.ttps.forEach(ttp => {
        const attackPattern = mapTTPToAttackPattern(ttp);
        objects.push(attackPattern);
        report.object_refs.push(attackPattern.id);
      });
    }

    // Enhanced AI TTPs
    const enhancedTTPs = enhancedAIStep?.details?.extractedData?.ttps || [];
    enhancedTTPs.forEach((ttp: string) => {
      const attackPattern = mapTTPToAttackPattern(ttp);
      objects.push(attackPattern);
      report.object_refs.push(attackPattern.id);
    });

    // 6. Indicator objects for IOCs
    if (post.iocs && post.iocs.length > 0) {
      post.iocs.forEach(ioc => {
        const indicator = mapIOCToIndicator(ioc, 85);
        objects.push(indicator);
        report.object_refs.push(indicator.id);
      });
    }

    // Enhanced AI IOCs
    const enhancedIOCs = enhancedAIStep?.details?.extractedData?.iocs || [];
    enhancedIOCs.forEach((ioc: string) => {
      const indicator = mapIOCToIndicator(ioc, enhancedAIStep?.details?.confidence || 75);
      objects.push(indicator);
      report.object_refs.push(indicator.id);
    });

    // 7. Indicators from threat_indicators array
    if (post.threat_indicators && post.threat_indicators.length > 0) {
      post.threat_indicators.forEach(ti => {
        const indicator = mapIOCToIndicator(ti.indicator_value, Math.round(ti.confidence_score * 100));
        // Override with more specific pattern if we have indicator_type
        if (ti.indicator_type && ti.indicator_type !== 'unknown') {
          indicator.pattern = `[${ti.indicator_type}:value = '${ti.indicator_value}']`;
        }
        indicator.created = ti.created_at;
        if (ti.verified) {
          indicator.confidence = Math.min(indicator.confidence + 10, 100);
        }
        objects.push(indicator);
        report.object_refs.push(indicator.id);
      });
    }

    // 8. Relationship objects to connect everything
    const relationships: any[] = [];
    
    // Connect report to observed data
    relationships.push({
      type: 'relationship',
      spec_version: '2.1',
      id: `relationship--${generateUUID()}`,
      created: timestamp,
      modified: timestamp,
      relationship_type: 'based-on',
      source_ref: reportId,
      target_ref: observedDataId,
      confidence: 95
    });

    // Add relationships to objects
    objects.push(...relationships);

    // Update report object refs to include all object IDs except the report itself
    report.object_refs = objects
      .filter(obj => obj.id !== reportId)
      .map(obj => obj.id);

    // Create the final bundle
    return {
      type: 'bundle',
      id: bundleId,
      spec_version: '2.1',
      objects: objects
    };
  }, [post, analysisSteps]);

  const copyToClipboard = async (content: string, section: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadSTIX = () => {
    const blob = new Blob([JSON.stringify(stixBundle, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stix-export-${post.id}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatConfidence = (confidence: number) => {
    if (confidence >= 90) return { level: 'Very High', color: 'text-green-700 bg-green-100' };
    if (confidence >= 75) return { level: 'High', color: 'text-blue-700 bg-blue-100' };
    if (confidence >= 60) return { level: 'Medium', color: 'text-yellow-700 bg-yellow-100' };
    if (confidence >= 40) return { level: 'Low', color: 'text-orange-700 bg-orange-100' };
    return { level: 'Very Low', color: 'text-red-700 bg-red-100' };
  };

  const renderObjectSummary = (obj: any) => {
    switch (obj.type) {
      case 'report':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">{obj.name}</h4>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatConfidence(obj.confidence).color}`}>
                {formatConfidence(obj.confidence).level} Confidence
              </span>
            </div>
            <p className="text-sm text-gray-600">{obj.description}</p>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span>Published: {new Date(obj.published).toLocaleString()}</span>
              <span>Objects: {obj.object_refs?.length || 0}</span>
            </div>
          </div>
        );
      
      case 'indicator':
        const iocMatch = obj.pattern.match(/'([^']+)'/);
        const iocValue = iocMatch ? iocMatch[1] : obj.pattern;
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">IOC: {obj.indicator_types?.[0] || 'Unknown Type'}</h4>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatConfidence(obj.confidence).color}`}>
                {obj.confidence}% Confidence
              </span>
            </div>
            <div className="bg-gray-800 text-green-400 p-2 rounded font-mono text-sm">
              {iocValue}
            </div>
            <p className="text-xs text-gray-500">Valid from: {new Date(obj.valid_from).toLocaleString()}</p>
          </div>
        );
      
      case 'attack-pattern':
        return (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">{obj.name}</h4>
            <p className="text-sm text-gray-600">{obj.description}</p>
            {obj.external_references?.map((ref: any, idx: number) => (
              <a
                key={idx}
                href={ref.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
              >
                {ref.external_id} - {ref.source_name}
              </a>
            ))}
          </div>
        );
      
      case 'malware':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">{obj.name}</h4>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatConfidence(obj.confidence).color}`}>
                {obj.confidence}% Confidence
              </span>
            </div>
            <p className="text-sm text-gray-600">{obj.description}</p>
            <div className="flex flex-wrap gap-1">
              {obj.malware_types?.map((type: string, idx: number) => (
                <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                  {type}
                </span>
              ))}
            </div>
          </div>
        );
      
      case 'threat-actor':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">{obj.name}</h4>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatConfidence(obj.confidence).color}`}>
                {obj.confidence}% Confidence
              </span>
            </div>
            <p className="text-sm text-gray-600">{obj.description}</p>
            <div className="flex flex-wrap gap-1">
              {obj.threat_actor_types?.map((type: string, idx: number) => (
                <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                  {type}
                </span>
              ))}
            </div>
          </div>
        );
      
      default:
        return (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">{obj.type}</h4>
            <p className="text-sm text-gray-600">{obj.description || obj.name || 'STIX object'}</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <FileJson className="w-5 h-5 text-blue-600" />
            <span>STIX 2.1 Export</span>
          </h3>
          <p className="text-sm text-gray-600">
            Structured Threat Information eXpression format for external systems integration
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowRawJSON(!showRawJSON)}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            {showRawJSON ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showRawJSON ? 'Hide JSON' : 'Show JSON'}
          </button>
          
          <button
            onClick={() => copyToClipboard(JSON.stringify(stixBundle, null, 2), 'full')}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {copiedSection === 'full' ? <CheckCircle className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copiedSection === 'full' ? 'Copied!' : 'Copy STIX'}
          </button>
          
          <button
            onClick={downloadSTIX}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-1" />
            Download
          </button>
        </div>
      </div>

      {/* STIX Bundle Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Shield className="w-5 h-5 text-blue-600" />
          <h4 className="font-medium text-blue-900">STIX Bundle Overview</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-blue-700 font-medium">Bundle ID</div>
            <div className="text-blue-600 font-mono text-xs break-all">{stixBundle.id}</div>
          </div>
          <div>
            <div className="text-blue-700 font-medium">STIX Version</div>
            <div className="text-blue-600">{stixBundle.spec_version}</div>
          </div>
          <div>
            <div className="text-blue-700 font-medium">Objects</div>
            <div className="text-blue-600">{stixBundle.objects.length}</div>
          </div>
          <div>
            <div className="text-blue-700 font-medium">TLP Level</div>
            <div className="text-blue-600">{mapThreatLevelToTLP(post.threat_level)}</div>
          </div>
        </div>
      </div>

      {/* Object Type Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(
          stixBundle.objects.reduce((acc: Record<string, number>, obj) => {
            acc[obj.type] = (acc[obj.type] || 0) + 1;
            return acc;
          }, {})
        ).map(([type, count]) => (
          <div key={type} className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm font-medium text-gray-900 capitalize">
              {type.replace('-', ' ')}
            </div>
            <div className="text-2xl font-bold text-gray-700">{count}</div>
          </div>
        ))}
      </div>

      {/* STIX Objects Details */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">STIX Objects</h4>
        
        {stixBundle.objects.map((obj, index) => (
          <div key={index} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection(`object-${index}`)}
              className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  obj.type === 'report' ? 'bg-blue-100 text-blue-700' :
                  obj.type === 'indicator' ? 'bg-red-100 text-red-700' :
                  obj.type === 'attack-pattern' ? 'bg-orange-100 text-orange-700' :
                  obj.type === 'malware' ? 'bg-red-100 text-red-700' :
                  obj.type === 'threat-actor' ? 'bg-purple-100 text-purple-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {obj.type}
                </span>
                <span className="font-medium text-gray-900">
                  {obj.name || obj.pattern || obj.id}
                </span>
              </div>
              {expandedSections.has(`object-${index}`) ? 
                <ChevronDown className="w-4 h-4 text-gray-500" /> : 
                <ChevronRight className="w-4 h-4 text-gray-500" />
              }
            </button>
            
            {expandedSections.has(`object-${index}`) && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  {/* Object Summary */}
                  <div className="bg-gray-50 p-3 rounded">
                    {renderObjectSummary(obj)}
                  </div>
                  
                  {/* Raw JSON */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Raw STIX Object</span>
                      <button
                        onClick={() => copyToClipboard(JSON.stringify(obj, null, 2), `object-${index}`)}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                      >
                        {copiedSection === `object-${index}` ? 
                          <CheckCircle className="w-3 h-3" /> : 
                          <Copy className="w-3 h-3" />
                        }
                        <span>{copiedSection === `object-${index}` ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
                      {JSON.stringify(obj, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Full JSON Export */}
      {showRawJSON && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Complete STIX Bundle (JSON)</h4>
            <button
              onClick={() => copyToClipboard(JSON.stringify(stixBundle, null, 2), 'json')}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
            >
              {copiedSection === 'json' ? 
                <CheckCircle className="w-4 h-4" /> : 
                <Copy className="w-4 h-4" />
              }
              <span>{copiedSection === 'json' ? 'Copied!' : 'Copy JSON'}</span>
            </button>
          </div>
          <pre className="bg-gray-900 text-green-400 p-4 rounded text-sm overflow-x-auto max-h-96">
            {JSON.stringify(stixBundle, null, 2)}
          </pre>
        </div>
      )}

      {/* Integration Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900 mb-2">Integration Notes</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• This STIX 2.1 bundle contains all threat intelligence extracted from the analyzed post</li>
              <li>• Compatible with STIX 2.1 specification and most threat intelligence platforms</li>
              <li>• Confidence scores are normalized to 0-100 scale for indicators and analysis results</li>
              <li>• MITRE ATT&CK technique mappings included where applicable</li>
              <li>• Traffic Light Protocol (TLP) marking based on threat level classification</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}